import React, { useEffect, useState, useMemo } from 'react';
import styles from './InfoBox.module.css';
import InfoBoxRow from './InfoBoxRow.jsx';
import { INFOBOX_DISPLAY_TYPE, PRODUCT_TYPES, CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES, VISIBLE_FIELDS } from '../../../constants/enums';
import numberFormatter from '../../../utils/numberFormatter.js';
import campaignValues from '../../../utils/campaignValues';
import infoBoxCommon from './InfoBoxCommon';
import { useLp5Configuration } from '../Contexts/Lp5ConfigurationContext';

const InfoBoxDK = ({ customerId, orderlinesData, initialOrderlinesData, orderData, selectedOrderlineIds,
    productType, campaignPromiseType, orderOrderLines, parameters }) => {
    const { visibleFields } = useLp5Configuration();
    const [postingData, setPostingData] = useState([]);
    const extraText = useMemo(() => {
        const hasUdda = orderlinesData.orderLineDataInformation.withUdda && false;
        return campaignValues.
            fetchEtxraTextForCampaignPromise(campaignPromiseType, CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES.DETAILS_TAB, hasUdda);
    }, [campaignPromiseType, orderlinesData])

    const [data, setData] = useState({
        vac: { displayType: null, target: null, delivered: null },
        altVac: { displayType: null, target: null, delivered: null },
        plays: { displayType: null, target: null, delivered: null },
        sov: { displayType: null, target: null, delivered: null },
        panels: { displayType: null, target: null, delivered: null },
        spotLength: { displayType: null, target: null, delivered: null },
        idsPanels: { displayType: null, target: null, delivered: null },
        panelsSov: { displayType: null, target: null, delivered: null }
    });

    const rowConfig = [
        { label: "VAC", key: "vac", formatter: numberFormatter.numberWithSpaces },
        { label: "5sec VAC", key: "altVac", formatter: numberFormatter.numberWithSpaces },
        { label: "Plays", key: "plays", formatter: numberFormatter.numberWithSpaces },
        { label: "SOV", key: "sov", formatter: numberFormatter.numberWithOneDecAndProcent },
        { label: "Panels", key: "panels", formatter: numberFormatter.numberWithOneDec },
        { label: "Spot Length", key: "spotLength", formatter: null },
        { label: "IDS Panels", key: "idsPanels", formatter: numberFormatter.numberWithOneDec },
        { label: `No of panels reaching min ${productType == PRODUCT_TYPES.CLASSIC ? parameters.postingSuccessLimit : parameters.playSuccessLimit}%`, key: "panelsSov", formatter: numberFormatter.numberWithSpaces }
    ];

    const getClassicVac = () => {
        return orderlinesData.orderLineDataInformation.podPostingsSuccessfulVAC;
    };

    useEffect(() => {
        if (productType == PRODUCT_TYPES.CLASSIC) {
            const mergedData = [
                ...orderlinesData.postingData,
                ...orderlinesData.podPostingData,
            ];

            const sortedData = mergedData.sort((a, b) => {
                const dateA = new Date(a.postingDate);
                const dateB = new Date(b.postingDate);
                return dateA - dateB;
            });
            setPostingData(sortedData);
        }
    }, [orderlinesData]);

    useEffect(() => {
        if (!orderOrderLines || !visibleFields || Object.keys(visibleFields).length === 0) return;
        const selectedOrderLine = orderOrderLines[0];
        const campaignInfo = orderlinesData?.orderLineDataInformation;
        const updatedData = { ...data };
        let spotLength = 0;
        let uniqueAidaSpotLengths = [];
        if (productType == PRODUCT_TYPES.PLAY) {
            if (orderlinesData.playData.spotlengths) {
                spotLength = orderlinesData.playData.spotlengths.length > 1 ?
                    'Mixed (' + orderlinesData.playData.spotlengths.join(', ') + ')' :
                    orderlinesData.playData.spotlengths[0];
            }
            if (initialOrderlinesData.playData.aidaSpotLengths) {
                uniqueAidaSpotLengths = [...new Set(initialOrderlinesData.playData.aidaSpotLengths.map(item => item.spotLength))].sort((a, b) => a - b);
            }
        }

        const setValues = (key, targetValue, deliveredValue, displayType) => {
            updatedData[key].target = displayType === INFOBOX_DISPLAY_TYPE.TARGET || displayType === INFOBOX_DISPLAY_TYPE.BOTH ? targetValue : null;
            updatedData[key].delivered = displayType === INFOBOX_DISPLAY_TYPE.DELIVERED || displayType === INFOBOX_DISPLAY_TYPE.BOTH ? deliveredValue : null;
            updatedData[key].displayType = displayType;
        };

        const audienceDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.AUDIENCE);
        if (audienceDisplay) {
            if (productType == PRODUCT_TYPES.CLASSIC) {
                setValues('vac', selectedOrderLine.postingTargetVac, getClassicVac(), audienceDisplay);
            } else {
                setValues('vac', selectedOrderLine.targetVAC, campaignInfo.vacContacts, audienceDisplay);
            }
        }

        const altAudienceDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.ALT_AUDIENCE);
        if (altAudienceDisplay && productType == PRODUCT_TYPES.PLAY) {
            setValues('altVac', null, orderlinesData.playData.totalAlternateVAC, altAudienceDisplay);
        }

        const playsDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.PLAYS);
        if (playsDisplay && productType == PRODUCT_TYPES.PLAY) {
            setValues('plays', selectedOrderLine.targetPlays ?? 0, campaignInfo.plays, playsDisplay);
        }

        const sovDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.SOV);
        if (sovDisplay && productType == PRODUCT_TYPES.PLAY) {
            setValues('sov', selectedOrderLine.targetSoV, campaignInfo.soV, sovDisplay);
        }

        const panelsDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.PANELS);
        if (panelsDisplay) {
            if (productType == PRODUCT_TYPES.CLASSIC) {
                setValues('panels', selectedOrderLine.postingTarget, infoBoxCommon.getClassicPanelsDelivered(postingData), panelsDisplay);
            } else {
                setValues('panels', selectedOrderLine.targetScreens, campaignInfo.notStarted ? 0 : campaignInfo.screens, panelsDisplay);
            }
        }

        const spotLengthDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.SPOT_LENGTH);
        if (spotLengthDisplay && productType == PRODUCT_TYPES.PLAY) {
            setValues('spotLength', infoBoxCommon.getSpotLength(uniqueAidaSpotLengths), spotLength, spotLengthDisplay);
        }

        const panelsSovDisplay = infoBoxCommon.getDisplayType(visibleFields, VISIBLE_FIELDS.PANELS_SOV);
        if (panelsSovDisplay && productType == PRODUCT_TYPES.PLAY) {
            const panelsReachSov = orderlinesData.playData.plays.panels.filter(p => p.reachedSov && p.externalKey !== null && p.externalKey !== '').length;
            setValues('panelsSov', null, panelsReachSov, panelsSovDisplay);
        }

        setData(updatedData);
    }, [orderOrderLines, orderlinesData, productType, visibleFields, postingData]);

    return (
        <div className={styles.infoWrapper}>
            <div className={styles.infoBoxContainer}>
                <h3>Campaign data</h3>
                <div className={styles.boxWrapper}>
                    <table>
                    <thead>
                        <tr><th></th><th><b>Estimated</b></th><th><b>Delivered</b></th></tr>
                    </thead>
                    <tbody>
                        {rowConfig.map(({ label, key, formatter }) => (
                            data[key].displayType && (
                                <InfoBoxRow
                                    key={key}
                                    label={label}
                                    targetValue={data[key].target}
                                    deliveredValue={data[key].delivered}
                                    displayType={data[key].displayType}
                                    formatter={formatter}
                                />
                            )
                        ))}
                    </tbody>
                </table>
            </div>
            </div>
            {extraText}
        </div>
    );
};

export default InfoBoxDK;
